import { useEffect, useState } from 'react';
import { noop } from 'lodash';
import { ApiWebSocket } from '@paradigm/api/src/internal/createWebSocket';
import { logException } from '@paradigm/logging/src/logging';
import {
  subscribeToChannels,
  unsubscribeFromChannels,
} from '#/websocket/useWsInstance';

export type SubscriptionStatus =
  | 'INITIAL'
  | 'SUBSCRIBING'
  | 'SUBSCRIBED'
  | 'ERROR';

export interface SubscriptionState {
  error: string;
  // may change this just to a boolean, experimenting for now.
  // we probably would want at least "OPEN", "CLOSED", "SUBSCRIBING" ?
  status: SubscriptionStatus;
}

const INITIAL_STATE: SubscriptionState = {
  status: 'INITIAL', // sometimes called `IDLE`
  error: '',
};

/**
 *
 * @param ws concrete websocket instance object
 * @param channels websocket channels meant to create subscribe requests
 * @param topicName unique topic name to identify the ws observable internally
 * @returns {SubscriptionState}
 */
export function useSubscriber<T extends ApiWebSocket<N>, N>(
  ws: T,
  channels: readonly string[],
  topicName: string,
): SubscriptionState {
  const [state, setState] = useState<SubscriptionState>(INITIAL_STATE);

  useEffect(() => {
    let abortController = new AbortController();
    const handleOpen = () => {
      abortController.abort();
      abortController = new AbortController();
      setState({
        status: 'SUBSCRIBING',
        error: '',
      });
      if (ws.readyState === WebSocket.OPEN) {
        subscribeToChannels(ws, channels, topicName, abortController.signal)
          .then((results) => {
            const didSucceed = results.every(
              ([, didSubscribe]) => didSubscribe,
            );
            if (didSucceed) {
              setState({
                status: 'SUBSCRIBED',
                error: '',
              });
            } else {
              setState({
                status: 'ERROR',
                error: 'Failed to subscribe from one of the channels',
              });
            }
          })
          .catch((error) => {
            setState({
              status: 'ERROR',
              error: `Unexpected failure subscribing to ${topicName} channels`,
            });
            logException(
              `Unexpected failure subscribing to ${topicName} channels`,
              error,
            );
          });
      }
    };

    const handleClose = () => {
      abortController.abort();
      setState({
        status: 'ERROR',
        error: 'Disconnected from Paradigm servers. Trying to reconnect…',
      });
    };

    const unsubscribe = async () => {
      return unsubscribeFromChannels(ws, channels, abortController.signal);
    };

    ws.addEventListener('open', handleOpen);
    ws.addEventListener('close', handleClose);

    return () => {
      ws.removeEventListener('open', handleOpen);
      ws.removeEventListener('close', handleClose);
      unsubscribe().then(noop).catch(noop);
    };
  }, [ws, setState, channels, topicName]);

  return state;
}
